import { useState, useEffect } from "react";
import { apiClient } from "../api/client";
import type { EvaluationPeriod } from "../api/types";
import Card from "../components/Card";
import { useAuth } from "../context/AuthContext";

function EvaluationPeriodManagement() {
  const { user } = useAuth();
  const [periods, setPeriods] = useState<EvaluationPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<EvaluationPeriod | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    status: "DRAFT" as "DRAFT" | "ACTIVE" | "CLOSED",
  });

  // Fetch periods
  const fetchPeriods = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<{ periods: EvaluationPeriod[] }>("/evaluation-periods");
      setPeriods(data.periods || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load evaluation periods");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPeriods();
  }, []);

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Convert date strings to ISO datetime format
      const payload = {
        ...formData,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : "",
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : "",
      };

      if (editingPeriod) {
        // Update existing
        await apiClient.patch(`/evaluation-periods/${editingPeriod.id}`, payload);
      } else {
        // Create new
        await apiClient.post("/evaluation-periods", payload);
      }
      alert(editingPeriod ? "Period updated!" : "Period created!");
      setShowForm(false);
      setEditingPeriod(null);
      resetForm();
      fetchPeriods();
    } catch (err: any) {
      alert(err.message || "Failed to save period");
    }
  };

  // Handle edit
  const handleEdit = (period: EvaluationPeriod) => {
    setEditingPeriod(period);
    setFormData({
      name: period.name,
      description: period.description || "",
      startDate: period.startDate.split("T")[0],
      endDate: period.endDate.split("T")[0],
      status: period.status,
    });
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this period?")) return;
    try {
      await apiClient.delete(`/evaluation-periods/${id}`);
      alert("Period deleted!");
      fetchPeriods();
    } catch (err: any) {
      alert(err.message || "Failed to delete period");
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      startDate: "",
      endDate: "",
      status: "DRAFT",
    });
  };

  // Cancel form
  const handleCancel = () => {
    setShowForm(false);
    setEditingPeriod(null);
    resetForm();
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "DRAFT":
        return "bg-yellow-100 text-yellow-700";
      case "ACTIVE":
        return "bg-green-100 text-green-700";
      case "CLOSED":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading evaluation periods...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Evaluation Periods</h2>
          <p className="text-sm text-slate-500 mt-1">Manage evaluation cycles and timeframes</p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            + Create Period
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <Card title={editingPeriod ? "Edit Evaluation Period" : "Create Evaluation Period"}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Period Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Q1 2025 Review"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.value as "DRAFT" | "ACTIVE" | "CLOSED",
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="DRAFT">Draft</option>
                  <option value="ACTIVE">Active</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Date *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                  placeholder="Optional description of this evaluation period..."
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                {editingPeriod ? "Update Period" : "Create Period"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Periods List */}
      <div className="grid gap-4">
        {periods.length === 0 ? (
          <Card>
            <div className="text-center py-8 text-slate-500">
              No evaluation periods found. Create one to get started.
            </div>
          </Card>
        ) : (
          periods.map((period) => (
            <Card key={period.id}>
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{period.name}</h3>
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                          period.status
                        )}`}
                      >
                        {period.status}
                      </span>
                    </div>
                    {period.description && (
                      <p className="text-sm text-slate-600 mb-2">{period.description}</p>
                    )}
                    <div className="text-sm text-slate-500 space-y-1">
                      <p>
                        <span className="font-medium">Period:</span>{" "}
                        {new Date(period.startDate).toLocaleDateString()} -{" "}
                        {new Date(period.endDate).toLocaleDateString()}
                      </p>
                      <p>
                        <span className="font-medium">Created:</span>{" "}
                        {new Date(period.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {user?.role === "ADMIN" && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(period)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(period.id)}
                        className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

export default EvaluationPeriodManagement;
